
import { useState, useEffect, useRef } from 'react';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<number | undefined>();
  const previousValueRef = useRef<T>(value);
  
  useEffect(() => {
    // If the value hasn't changed, don't trigger a debounce
    if (value === previousValueRef.current) {
      return;
    }
    
    // Update the previous value reference
    previousValueRef.current = value;
    
    // Clear any existing timeout to avoid multiple updates
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Empty string should be propagated immediately for better UX
    if (typeof value === 'string' && value === '') {
      setDebouncedValue(value);
      return;
    }
    
    // Very short inputs (1-2 chars) should use a shorter delay for better responsiveness
    // Longer inputs use the full delay to avoid unnecessary API calls
    const actualDelay = typeof value === 'string' && 
                       (value as string).length <= 2 ? 
                       Math.min(150, delay) : delay;
    
    // Set new timeout with requestAnimationFrame for better performance
    const rafId = requestAnimationFrame(() => {
      timeoutRef.current = window.setTimeout(() => {
        setDebouncedValue(value);
      }, actualDelay);
    });
    
    // Clean up timeout and animation frame when component unmounts or value changes
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);
  
  return debouncedValue;
}
